/**
 * Analytics event tracking for plan restriction UI components
 *
 * This module provides type-safe event tracking functions for plan restriction
 * interactions using PostHog analytics.
 *
 * @see docs/rfcs/rfc-077-plan-restriction-ui-system.md
 */

import { trackEvent } from '@/lib/posthog';

export type Plan = 'FREE' | 'STARTER' | 'TEAM' | 'PRO' | 'ENTERPRISE' | 'CUSTOM';

export type LimitType =
  | 'sops'
  | 'workspaces'
  | 'members'
  | 'storage'
  | 'aiCredits'
  | 'privateReaders';

/**
 * Track when an upgrade prompt is shown to the user
 */
export function trackUpgradePromptShown(params: {
  feature: string;
  source: string;
  currentPlan: Plan;
  requiredPlan: Plan;
  featureFlag?: string;
}) {
  trackEvent('upgrade_prompt_shown', {
    feature: params.feature,
    source: params.source,
    current_plan: params.currentPlan,
    required_plan: params.requiredPlan,
    feature_flag: params.featureFlag,
  });
}

/**
 * Track when user clicks the upgrade button
 */
export function trackUpgradeButtonClicked(params: {
  feature: string;
  source: string;
  targetPlan: Plan;
  currentPlan?: Plan;
  featureFlag?: string;
}) {
  trackEvent('upgrade_button_clicked', {
    feature: params.feature,
    source: params.source,
    target_plan: params.targetPlan,
    current_plan: params.currentPlan,
    feature_flag: params.featureFlag,
  });
}

/**
 * Track successful upgrade completion
 */
export function trackUpgradeCompleted(params: {
  previousPlan: Plan;
  newPlan: Plan;
  source: string;
  feature?: string;
}) {
  trackEvent('upgrade_completed', {
    previous_plan: params.previousPlan,
    new_plan: params.newPlan,
    source: params.source,
    feature: params.feature,
  });
}

/**
 * Track when a limit warning is shown to the user
 */
export function trackLimitWarningShown(params: {
  limitType: LimitType;
  usagePercent: number;
  plan: Plan;
  current: number;
  max: number;
}) {
  trackEvent('limit_warning_shown', {
    limit_type: params.limitType,
    usage_percent: params.usagePercent,
    plan: params.plan,
    current: params.current,
    max: params.max,
  });
}

/**
 * Track when user attempts an action but limit is exceeded
 */
export function trackLimitExceeded(params: {
  limitType: LimitType;
  plan: Plan;
  actionAttempted: string;
  current: number;
  max: number;
}) {
  trackEvent('limit_exceeded', {
    limit_type: params.limitType,
    plan: params.plan,
    action_attempted: params.actionAttempted,
    current: params.current,
    max: params.max,
  });
}

/**
 * Track when user views a locked feature
 */
export function trackLockedFeatureViewed(params: {
  feature: string;
  requiredPlan: Plan;
  source: string;
  currentPlan?: Plan;
  featureFlag?: string;
}) {
  trackEvent('locked_feature_viewed', {
    feature: params.feature,
    required_plan: params.requiredPlan,
    source: params.source,
    current_plan: params.currentPlan,
    feature_flag: params.featureFlag,
  });
}

/**
 * Track when user interacts with a feature list item
 */
export function trackFeatureListItemClicked(params: {
  feature: string;
  isLocked: boolean;
  action: 'learn_more' | 'upgrade' | 'enable' | 'disable';
  requiredPlan?: Plan;
  currentPlan?: Plan;
  featureFlag?: string;
}) {
  trackEvent('feature_list_item_clicked', {
    feature: params.feature,
    is_locked: params.isLocked,
    action: params.action,
    required_plan: params.requiredPlan,
    current_plan: params.currentPlan,
    feature_flag: params.featureFlag,
  });
}

/**
 * Track when user dismisses an upgrade prompt
 */
export function trackUpgradePromptDismissed(params: {
  feature: string;
  source: string;
  requiredPlan: Plan;
  currentPlan?: Plan;
  timeOnScreen?: number; // milliseconds
}) {
  trackEvent('upgrade_prompt_dismissed', {
    feature: params.feature,
    source: params.source,
    required_plan: params.requiredPlan,
    current_plan: params.currentPlan,
    time_on_screen_ms: params.timeOnScreen,
  });
}

/**
 * Track when user views plan badge
 */
export function trackPlanBadgeViewed(params: {
  plan: Plan;
  variant: 'default' | 'compact' | 'dot';
  source: string;
  feature?: string;
}) {
  trackEvent('plan_badge_viewed', {
    plan: params.plan,
    variant: params.variant,
    source: params.source,
    feature: params.feature,
  });
}

/**
 * Track when user interacts with limit progress card
 */
export function trackLimitProgressCardViewed(params: {
  limitType: LimitType;
  current: number;
  max: number;
  usagePercent: number;
  plan: Plan;
  showingUpgrade: boolean;
}) {
  trackEvent('limit_progress_card_viewed', {
    limit_type: params.limitType,
    current: params.current,
    max: params.max,
    usage_percent: params.usagePercent,
    plan: params.plan,
    showing_upgrade: params.showingUpgrade,
  });
}

/**
 * Track when feature gate modal is shown
 */
export function trackFeatureGateModalShown(params: {
  feature: string;
  featureFlag: string;
  requiredPlan: Plan;
  currentPlan?: Plan;
  source: string;
}) {
  trackEvent('feature_gate_modal_shown', {
    feature: params.feature,
    feature_flag: params.featureFlag,
    required_plan: params.requiredPlan,
    current_plan: params.currentPlan,
    source: params.source,
  });
}

/**
 * Track when inline feature lock is displayed
 */
export function trackInlineFeatureLockShown(params: {
  feature: string;
  requiredPlan: Plan;
  currentPlan?: Plan;
  variant: 'card' | 'banner' | 'inline';
  source: string;
}) {
  trackEvent('inline_feature_lock_shown', {
    feature: params.feature,
    required_plan: params.requiredPlan,
    current_plan: params.currentPlan,
    variant: params.variant,
    source: params.source,
  });
}
